// Touch controls for phones (#67): a floating stick under the left thumb and
// the THPS buttons under the right, read into the same `RawInput` as a pad or
// the keyboard, so the sim, the trick tables and the tests don't change.
//
// Sim-free and DOM-free: `ui/TouchControls.tsx` feeds pointer events in (in
// the overlay's own pixels) and draws what `TouchPad` says is held. The layout
// is plain numbers from the screen size, so hit tests are testable.

import type { Cue } from "./sound/cues";
import { DEADZONE, emptyRaw, type Button, type RawInput } from "./input";
import { TOUCH_STEER_FEEL } from "./settings";

/** The buttons on glass. Pause and help stay the icon buttons at the top. `stepOff` is both triggers at once (#19). */
export type TouchButton = "ollie" | "flip" | "grab" | "grind" | "spinL" | "spinR" | "revert" | "nollie" | "stepOff";

export interface TouchKey {
  button: TouchButton;
  /** Center and radius, in overlay pixels. */
  x: number;
  y: number;
  r: number;
  /** The action word printed on it. */
  label: string;
}

export interface TouchLayout {
  keys: TouchKey[];
  /** How far the stick's knob travels from where the thumb landed, in pixels: full lock at the rim. */
  stickRadius: number;
  /** The stick's response curve (#226): how far it's pushed, raised to this power. */
  stickCurve: number;
  /** How big the stick's ring is drawn, in pixels (#226: the old throw, so a longer one doesn't draw a bigger stick). */
  stickRing: number;
  /** Touches between these (that don't land on a key) grab the stick: the left half, or the right one left-handed. */
  stickMinX: number;
  stickMaxX: number;
}

export interface TouchLayoutOptions {
  /** Button and stick size, from Settings (`TOUCH_SIZE_SCALE`); 1 is medium. */
  scale?: number;
  /** Left-handed: the buttons on the left, the stick on the right. */
  left?: boolean;
  /** Only these keys (Bomb the Hill's, #198), each where it sits in the full layout. */
  only?: ReadonlySet<TouchButton>;
  /** The stick's throw (in units) and response curve, from Settings' steering sensitivity (#226). */
  steer?: { throw: number; curve: number };
  /**
   * The device's short side in CSS pixels (`screen`, not the viewport), which
   * the stick's throw is sized from (#230), so the same thumb movement steers
   * the same held upright or sideways, toolbar showing or not. Left out, the
   * viewport's short side.
   */
  device?: number;
}

/**
 * The keys Bomb the Hill uses (#198): Ollie, Flip and Grab for tricks off
 * its ramps, and Grind for its rails down the hill (#237). Spins, reverts
 * and stepping off don't come up on a run down the hill.
 */
export const HILL_KEYS: ReadonlySet<TouchButton> = new Set<TouchButton>(["ollie", "flip", "grab", "grind"]);

/** Room kept clear of the screen edges (and notches), in pixels. */
const MARGIN = 14;

/** No button smaller than 44 pt (iOS' thumb-target floor), whatever the size setting does. */
const MIN_R = 22;

/** The top icon row (pause, help, fullscreen) owns this band; keys stay clear of it. */
const TOP_CLEAR = 70;

/** The least clear space between two keys' circles, in pixels. */
const GAP = 8;

/**
 * Where the keys sit on a `w` x `h` overlay, laid out for a thumb resting in
 * the bottom-right corner (#162):
 *
 * - The **face diamond** sits low in that corner, under the thumb's natural
 *   arc: Ollie (A) biggest at the bottom nearest the corner, Flip (X) left,
 *   Grab (B) right, Grind (Y) just above, with Step off (#19) low beside
 *   Flip toward the stick. These are the buttons you hit every second.
 * - The **shoulder keys** (L/R spins, Nollie, Revert), the ones you reach
 *   for now and then, sit in one row right under the top icon row, away
 *   from the thumb's path: L, R, Nollie, Revert. They used to be a 2x2
 *   grid there, but on a short screen (landscape Safari with its toolbar
 *   showing) the grid's bottom row ran into Grind; one row takes half the
 *   height.
 *
 * When the screen is too short for both, the diamond's spacing shrinks
 * until it fits (never below the point where its buttons would touch),
 * so nothing ever overlaps.
 *
 * Left-handed mirrors the whole thing to the left edge, keeping each cluster
 * the same way round (Flip left of Grab, L left of R). Spacing has its own
 * floor so the buttons never crowd once sizes hit the 44 pt minimum.
 */
export function touchLayout(w: number, h: number, { scale = 1, left = false, only, steer = TOUCH_STEER_FEEL.medium, device }: TouchLayoutOptions = {}): TouchLayout {
  // One unit is about a thumb: 11% of the short side, 40-64 px, then the size setting.
  const thumb = (short: number) => Math.min(64, Math.max(40, short * 0.11)) * scale;
  const u = thumb(Math.min(w, h));
  const face = Math.max(MIN_R, 0.56 * u);
  const small = Math.max(MIN_R, 0.48 * u);
  const rOllie = Math.max(MIN_R, 0.7 * u);
  // The stick's unit comes from the device, not the viewport (#230): a toolbar or the way it's held doesn't change the throw.
  const su = device && device > 0 ? thumb(device) : u;
  const stick = { stickRadius: steer.throw * su, stickCurve: steer.curve, stickRing: 1.1 * su };
  if (only && h > w) return { ...uprightLayout(w, h, face, rOllie, left, only), ...stick };

  // Shoulder row, under the top icon row, ending at the buttons' outer edge.
  const rowY = TOP_CLEAR + 10 + small;
  const rowStep = 2 * small + GAP;
  const rowEnd = left ? MARGIN + small : w - MARGIN - small;
  const rowX = (i: number) => (left ? rowEnd + i * rowStep : rowEnd - (3 - i) * rowStep);

  // Spacing unit, floored so the gaps hold up when small sizes hit the 44 pt radius floor,
  // then squeezed on short screens so Grind's top clears the shoulder row. The diamond's
  // bottom (Ollie or Step off) sits `drop` above the bottom margin and Grind 0.98 g above
  // `cy`, so the diamond's height is drop + 0.98 g + Grind's radius.
  const drop = (g: number) => Math.max(1.95 * g, 1.08 * g + rOllie, 1.15 * g + small);
  const avail = h - MARGIN - (rowY + small + GAP) - face;
  const fit = Math.min(avail / 2.93, (avail - rOllie) / 2.06, (avail - small) / 2.13);
  // Never so tight that neighbours touch: Flip/Grab sit about 1.6 g from Grind and Ollie,
  // Step off about 1.06 g from Flip.
  const gMin = Math.max((face + rOllie + GAP) / 1.6, (small + face + GAP) / 1.06);
  const g = Math.max(gMin, Math.min(Math.max(u, 50), fit));

  // Face diamond, low in the thumb's corner.
  const cx = left ? MARGIN + 1.95 * g : w - MARGIN - 1.95 * g;
  const cy = h - MARGIN - drop(g);

  const keys: TouchKey[] = [
    { button: "ollie", x: cx, y: cy + 1.08 * g, r: rOllie, label: "Ollie" },
    { button: "flip", x: cx - 1.28 * g, y: cy + 0.1 * g, r: face, label: "Flip" },
    { button: "grab", x: cx + 1.28 * g, y: cy + 0.1 * g, r: face, label: "Grab" },
    { button: "grind", x: cx, y: cy - 0.98 * g, r: face, label: "Grind" },
    { button: "stepOff", x: cx - 1.45 * g, y: cy + 1.15 * g, r: small, label: "Step off" },
    { button: "spinL", x: rowX(0), y: rowY, r: small, label: "L" },
    { button: "spinR", x: rowX(1), y: rowY, r: small, label: "R" },
    { button: "nollie", x: rowX(2), y: rowY, r: small, label: "Nollie" },
    { button: "revert", x: rowX(3), y: rowY, r: small, label: "Revert" },
  ];
  return {
    keys: only ? keys.filter((k) => only.has(k.button)) : keys,
    ...stick,
    stickMinX: left ? w / 2 : 0,
    stickMaxX: left ? w : w / 2,
  };
}

/**
 * Bomb the Hill held upright (#213): a one-thumb layout. Ollie sits biggest
 * in the bottom corner, Flip beside it toward the middle, Grab above it and
 * Grind (#237) between them up toward the middle,
 * all under one thumb's arc; a drag anywhere else steers, so a thumb sliding
 * along the bottom of a tall screen carves. Left-handed mirrors it.
 */
function uprightLayout(w: number, h: number, face: number, rOllie: number, left: boolean, only: ReadonlySet<TouchButton>): Omit<TouchLayout, "stickRadius" | "stickCurve" | "stickRing"> {
  const side = left ? 1 : -1;
  const ox = left ? MARGIN + rOllie : w - MARGIN - rOllie;
  const oy = h - MARGIN - rOllie;
  const reach = rOllie + face + GAP + 4;
  const keys: TouchKey[] = [
    { button: "ollie", x: ox, y: oy, r: rOllie, label: "Ollie" },
    { button: "flip", x: ox + side * reach * 0.94, y: oy + rOllie - face - reach * 0.34, r: face, label: "Flip" },
    { button: "grab", x: ox + side * reach * 0.12, y: oy - reach, r: face, label: "Grab" },
    // Grind (#237) up between Flip and Grab, toward the middle: still under the thumb's arc, off the road's line.
    { button: "grind", x: ox + side * reach * 1.2, y: oy - reach * 1.1, r: face, label: "Grind" },
  ];
  return { keys: keys.filter((k) => only.has(k.button)), stickMinX: 0, stickMaxX: w };
}

/** A touch counts as on a key a little outside its drawn circle: thumbs are wide. */
const SLOP = 1.2;

/** The key under (x, y), if any: the nearest one whose (slopped) circle holds it. */
export function keyAt(layout: TouchLayout, x: number, y: number): TouchKey | null {
  let best: TouchKey | null = null;
  let bestD = Infinity;
  for (const k of layout.keys) {
    const d = Math.hypot(x - k.x, y - k.y);
    if (d <= k.r * SLOP && d < bestD) {
      best = k;
      bestD = d;
    }
  }
  return best;
}

/** Half the width of each straight direction's sector, in radians: 30°, so a diagonal is only 45° ± 15° (#226). */
const STRAIGHT_HALF = Math.PI / 6;
/** How far past its sector's edge a drag must go to leave the direction it's in (#226), so a wobble on the line doesn't flick between two. */
const STICK_HYSTERESIS = (6 * Math.PI) / 180;

/** The half-width of direction `i`'s sector (0 is right, counting anticlockwise in eighths). */
const sectorHalf = (i: number) => (i % 2 === 0 ? STRAIGHT_HALF : Math.PI / 4 - STRAIGHT_HALF);

/** How far angle `a` is from direction `i`, in radians (0 to π). */
function offDir(a: number, i: number): number {
  const d = Math.abs(a - (i * Math.PI) / 4) % (2 * Math.PI);
  return d > Math.PI ? 2 * Math.PI - d : d;
}

/**
 * Which of the eight directions angle `a` reads as (0 is right, counting
 * anticlockwise in eighths). Straight up, down, left and right get the
 * widest sectors (#226): a crooked sideways drag is still sideways, not a
 * diagonal that also pushes or brakes. `prev`, the direction it read last,
 * holds until the drag is `STICK_HYSTERESIS` past its edge.
 */
export function stickDirection(a: number, prev: number | null = null): number {
  if (prev !== null && offDir(a, prev) <= sectorHalf(prev) + STICK_HYSTERESIS) return prev;
  for (let i = 0; i < 8; i++) if (offDir(a, i) <= sectorHalf(i)) return i;
  return 0;
}

/**
 * A stick drag of (dx, dy) screen pixels (y down) as stick axes (+y is up):
 * the pad's deadzone, then eased by `curve` (#226: gentle near the centre,
 * full at the rim, `radius`) and snapped to one of eight directions
 * (`stickDirection`) so grinds, flips and specials read cleanly. `dir` is
 * that direction (null in the deadzone), for the next call's `prev`.
 */
export function stickAxes(dx: number, dy: number, radius: number, curve = 1, prev: number | null = null): { x: number; y: number; dir: number | null } {
  const m = Math.min(1, Math.hypot(dx, dy) / radius);
  if (m < DEADZONE) return { x: 0, y: 0, dir: null };
  const mag = ((m - DEADZONE) / (1 - DEADZONE)) ** curve;
  const dir = stickDirection(Math.atan2(-dy, dx), prev);
  const a = (dir * Math.PI) / 4;
  const clean = (v: number) => (Math.abs(v) < 1e-9 ? 0 : v);
  return { x: clean(Math.cos(a) * mag), y: clean(Math.sin(a) * mag), dir };
}

/**
 * How fast the stick's push follows the thumb (#226), in seconds: a little
 * smoothing so the rider doesn't twitch with every wobble. Only small
 * changes (under `STICK_JUMP` of full push) are smoothed, and only how far
 * it's pushed, never which way: a deliberate move, a flick that picks a
 * trick, a balance correction on a rail and letting go all land at once.
 */
export const STICK_SMOOTHING = 0.03;
export const STICK_JUMP = 0.15;

type Finger = { kind: "stick"; ox: number; oy: number; x: number; y: number } | { kind: "key"; key: TouchButton | null };

/** What the overlay draws: the stick (where it landed and where the knob is, scaled to `stickRing`) and the held keys. */
export interface TouchView {
  stick: { ox: number; oy: number; kx: number; ky: number } | null;
  held: ReadonlySet<TouchButton>;
}

/** The fingers on the overlay, by pointer id. `read()` once per frame. */
export class TouchPad {
  layout: TouchLayout = touchLayout(844, 390);
  private fingers = new Map<number, Finger>();
  /** Keys that went down since the last read, so a tap shorter than a frame still counts. */
  private tapped = new Set<TouchButton>();
  /** True once any touch has landed on the overlay. */
  used = false;
  /** The stick's direction last read (`stickAxes`' `dir`) and its smoothed push. */
  private stickDir: number | null = null;
  private stickMag = 0;

  /** A finger lands: on a key it holds that key, otherwise on the stick's half it grabs the stick. */
  down(id: number, x: number, y: number): void {
    this.used = true;
    const key = keyAt(this.layout, x, y);
    if (key) {
      this.fingers.set(id, { kind: "key", key: key.button });
      this.tapped.add(key.button);
    } else if (x >= this.layout.stickMinX && x < this.layout.stickMaxX && ![...this.fingers.values()].some((f) => f.kind === "stick")) {
      this.fingers.set(id, { kind: "stick", ox: x, oy: y, x, y });
    } else {
      // A miss on the buttons' side: it can still slide onto a key.
      this.fingers.set(id, { kind: "key", key: null });
    }
  }

  /**
   * A finger moves: the stick follows. A key finger keeps its key while it
   * drifts off it (thumbs wander while crouching) and switches when it
   * slides onto another one, which counts as a press: Ollie to Flip is
   * release-to-pop then the flip, as on the pad.
   */
  move(id: number, x: number, y: number): void {
    const f = this.fingers.get(id);
    if (!f) return;
    if (f.kind === "stick") {
      f.x = x;
      f.y = y;
      return;
    }
    const key = keyAt(this.layout, x, y)?.button ?? null;
    if (key && key !== f.key) {
      this.tapped.add(key);
      f.key = key;
    }
  }

  up(id: number): void {
    this.fingers.delete(id);
  }

  /** Lets go of everything (the overlay hid, the page lost focus). */
  clear(): void {
    this.fingers.clear();
    this.tapped.clear();
    this.stickDir = null;
    this.stickMag = 0;
  }

  private stickFinger(): Extract<Finger, { kind: "stick" }> | null {
    for (const f of this.fingers.values()) if (f.kind === "stick") return f;
    return null;
  }

  private heldKeys(): Set<TouchButton> {
    const held = new Set<TouchButton>();
    for (const f of this.fingers.values()) if (f.kind === "key" && f.key) held.add(f.key);
    return held;
  }

  /**
   * This frame's reading, merged into the pad and keyboard by `buildFrame`.
   * `dt` is the time since the last read, in seconds, for the stick's
   * smoothing (`STICK_SMOOTHING`); 0 reads the thumb as it is.
   */
  read(dt = 0): RawInput {
    const raw = emptyRaw();
    const s = this.stickFinger();
    const a = s ? stickAxes(s.x - s.ox, s.y - s.oy, this.layout.stickRadius, this.layout.stickCurve, this.stickDir) : null;
    if (a && a.dir !== null) {
      const want = Math.hypot(a.x, a.y);
      // Fresh from the centre, turned another way, or moved more than a wobble, the push goes where the thumb is.
      const wobble = this.stickDir === a.dir && Math.abs(want - this.stickMag) < STICK_JUMP;
      const k = dt > 0 && wobble ? 1 - Math.exp(-dt / STICK_SMOOTHING) : 1;
      this.stickMag += (want - this.stickMag) * k;
      const scale = want > 0 ? this.stickMag / want : 0;
      raw.moveX = a.x * scale;
      raw.moveY = a.y * scale;
      this.stickDir = a.dir;
    } else {
      this.stickDir = null;
      this.stickMag = 0;
    }
    for (const k of [...this.heldKeys(), ...this.tapped]) {
      // One tap for L2 + R2 together: the sim reads the chord's two presses in the same step.
      if (k === "stepOff") raw.held.nollie = raw.held.revert = true;
      else raw.held[k as Button] = true;
    }
    this.tapped.clear();
    return raw;
  }

  /** For drawing: doesn't consume taps. */
  view(): TouchView {
    const s = this.stickFinger();
    let stick: TouchView["stick"] = null;
    if (s) {
      const r = this.layout.stickRadius;
      const d = Math.hypot(s.x - s.ox, s.y - s.oy);
      // Full lock puts the knob on the ring's rim, however long the throw.
      const k = (d > r ? r / d : 1) * (this.layout.stickRing / r);
      stick = { ox: s.ox, oy: s.oy, kx: s.ox + (s.x - s.ox) * k, ky: s.oy + (s.y - s.oy) * k };
    }
    return { stick, held: this.heldKeys() };
  }
}

/** Short buzzes (ms, or a pattern) for the moments you feel through the board. */
export const HAPTICS: Partial<Record<Cue, number | number[]>> = {
  land: 12,
  perfect: 18,
  sloppy: 28,
  grind: 10,
  bail: [40, 30, 60],
  wobble: [20, 20, 20, 20, 20],
};
